import {
  VStack,
  Button,
  Box,
  Grid,
  Checkbox,
  Text,
  Spinner,
} from '@chakra-ui/react';
import { useNavigate } from 'react-router';
import { useEffect, useState, useRef } from 'react';
import { useMfa } from '../../../context/MfaContext';
import { useMfaApi } from '../../../hooks/useMfaApi';
import { useMfaToast } from '../../../hooks/useMfaToast';

function MfaRecoveryCode({ recoveryCodes, onComplete }) {
  const [codes, setCodes] = useState(recoveryCodes || []);
  const [isChecked, setIsChecked] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const navigate = useNavigate();
  const { completeMfaSetup } = useMfa();
  const { fetchRecoveryCodes } = useMfaApi();
  const { showSuccessToast, showErrorToast } = useMfaToast();

  const fetchRecoveryCodesRef = useRef();

  fetchRecoveryCodesRef.current = async () => {
    try {
      setIsLoading(true);
      const data = await fetchRecoveryCodes();
      setCodes(data.codes);
    } catch (error) {
      showErrorToast(
        'Error fetching recovery codes',
        error.response?.data?.message || 'Failed to fetch recovery codes'
      );
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (!recoveryCodes || recoveryCodes.length === 0) {
      fetchRecoveryCodesRef.current();
    } else {
      setCodes(recoveryCodes);
    }
  }, [recoveryCodes]);

  const copyCodes = () => {
    navigator.clipboard.writeText(codes.join('\n'));
    showSuccessToast('Recovery codes copied to clipboard');
  };

  const handleContinue = () => {
    completeMfaSetup();
    if (onComplete) {
      onComplete();
    } else {
      navigate('/dashboard', { replace: true });
      showSuccessToast(
        'MFA setup completed successfully',
        'Your account is now protected with multi-factor authentication'
      );
    }
  };

  return (
    <VStack spacing={4} alignItems="flex-start" w="full">
      <Text textStyle="bodySmall" color="#666666">
        These recovery codes will only be shown once. Keep them somewhere safe!
        You'll need them if you ever can't access your device.
      </Text>
      <VStack spacing={4} alignItems="flex-start" w="full" mb={4}>
        <Box
          bg="secondaryBackground"
          p={4}
          borderRadius="md"
          w="full"
          my={4}
          display="flex"
          justifyContent="center"
        >
          {isLoading ? (
            <Spinner />
          ) : (
            <Grid
              templateColumns="repeat(2, 1fr)"
              columnGap={4}
              rowGap={4}
              w="full"
              py={4}
            >
              {codes.map((code) => (
                <Text
                  key={code}
                  fontFamily="mono"
                  textStyle="bodySmall"
                  color="#666666"
                  textAlign="center"
                >
                  {code}
                </Text>
              ))}
            </Grid>
          )}
        </Box>
        <Button
          onClick={copyCodes}
          color="#666666"
          bg="softWhite"
          w="50%"
          borderColor="#666666"
          borderWidth="1px"
          fontWeight="normal"
          _hover={{ borderColor: 'brand', color: 'brand' }}
        >
          Copy codes
        </Button>
        <Checkbox
          isChecked={isChecked}
          onChange={() => setIsChecked(!isChecked)}
        >
          I have kept my code somewhere safe
        </Checkbox>
      </VStack>
      <Button onClick={handleContinue} isDisabled={!isChecked}>
        Continue
      </Button>
    </VStack>
  );
}

export default MfaRecoveryCode;
